import { createAsyncThunk } from '@reduxjs/toolkit';

import { CareerPath } from '@backrs_ui/common-ui';

import {
  CareerPathEnjoyment,
  CareerPathMeaning,
  CareerPathRole,
  CareerPathWithScore,
  CareerQuestion,
  CareerUserResponseSet,
} from '@backrs_ui/grow-ui/src/models/models';
import { withSettledStartTerm } from '@backrs_ui/grow-ui/src/pages/Survey/survey.util';
import { BlueprintAspirationsCategories } from '@backrs_ui/grow-ui/src/redux/blueprint/blueprint.lubav.api';
import {
  ScoreCalculationResult,
  calculateSurveyResults,
  determineTierFromResults,
} from '@backrs_ui/grow-ui/src/utils/calculateSurveyResults';
import { getCareerPathScores } from '@backrs_ui/grow-ui/src/utils/careerPath/careerPathCalculator';
import { determineOwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';
import { mintGuestSession } from '@backrs_ui/grow-ui/src/utils/guestSession';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { toCareerPathResults } from './updateResponseSet.thunk';

const ENVIRONMENT_URL = process.env.REACT_APP_API_BASEURL;

export type MostRecentResponseSetAPIResponse = Readonly<{
  userResponseSet: CareerUserResponseSet;
  aspirations_categories?: BlueprintAspirationsCategories;
}>;

type PostAPISurveyResultsInput = Readonly<{
  userResponseSet: CareerUserResponseSet;
  careerQuestions: CareerQuestion[];
  careerPaths: CareerPath[];
  careerPathRoles: CareerPathRole[];
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
}>;

export const getSurveyResults = ({
  userResponseSet,
  careerQuestions,
  careerPaths,
  careerPathRoles,
  careerPathMeanings,
  careerPathEnjoyments,
}: PostAPISurveyResultsInput) => {
  const careerResults: ScoreCalculationResult = calculateSurveyResults(
    userResponseSet,
    careerQuestions,
  );

  const tier = determineTierFromResults(careerResults);

  const careerPathSuggestions: CareerPathWithScore[] = getCareerPathScores({
    tier,
    careerPaths,
    careerPathRoles,
    userResponseSet,
    careerResults,
    questions: careerQuestions,
    careerPathMeanings,
    careerPathEnjoyments,
  });

  return { careerResults, tier, careerPathSuggestions };
};

export const postAPISurveyResults = createAsyncThunk(
  'userResponse/postAPISurveyResults',
  async (
    {
      userResponseSet,
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
    }: PostAPISurveyResultsInput,
    { rejectWithValue },
  ) => {
    try {
      const { careerResults, tier, careerPathSuggestions } = getSurveyResults({
        userResponseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      });

      // Persist for both authenticated users and guest visitors: the guest token
      // ties the blueprint to the guest session so it can be claimed on signup.
      // Mint a guest session if the visitor doesn't have any token yet.
      const accessToken = getBackrsToken() ?? (await mintGuestSession());

      if (accessToken) {
        const mappedCareerPaths = toCareerPathResults(careerPathSuggestions);
        const ownershipLevel = determineOwnershipLevel(tier, userResponseSet, careerQuestions);

        const response = await fetch(`${ENVIRONMENT_URL}/career-user-response-set`, {
          method: 'POST',
          body: JSON.stringify({
            user_response_set: withSettledStartTerm(userResponseSet),
            tier,
            entrepreneurship_recommended: careerResults.entrepreneurship,
            career_path_results: mappedCareerPaths,
            ownership_level: ownershipLevel,
          }),
        });

        if (!response.ok) {
          const errorBody = await response.text().catch(() => '');

          throw new Error(
            `Failed to post survey results: ${response.status} ${response.statusText}\n${errorBody}`,
          );
        }

        const responseSet = (await response.json()) as CareerUserResponseSet;

        return {
          userResponseSet: responseSet, // update the store with the response set from the API
          results: { careerResults, tier, careerPathSuggestions },
        };
      }

      // No token available (guest mint failed) — the blueprint can't be persisted
      // and won't survive sign-up.
      console.error('Blueprint not persisted: no auth token and guest session mint failed.');

      return {
        userResponseSet,
        results: { careerResults, tier, careerPathSuggestions },
      };
    } catch (error) {
      return rejectWithValue(`Failed to post survey results with error: ${error}`);
    }
  },
);

type GetUserResponseSetFromAPIInput = Readonly<{
  userResponseSetId: string;
  careerQuestions: CareerQuestion[];
  careerPaths: CareerPath[];
  careerPathRoles: CareerPathRole[];
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
}>;

/**
 * Fetches a user response set from the API and calculates the survey results.
 * We then take those results and calculate them. The reducer then listens for this action and updates the store with the results.
 */
export const getUserResponseSetFromAPI = createAsyncThunk(
  'userResponse/getUserResponseSetFromAPI',
  async (
    {
      userResponseSetId,
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
    }: GetUserResponseSetFromAPIInput,
    { rejectWithValue },
  ) => {
    try {
      const response = await fetch(
        `${ENVIRONMENT_URL}/career-user-response-set/${userResponseSetId}`,
        {
          method: 'GET',
        },
      );

      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');

        throw new Error(
          `Failed to get upload URL: ${response.status} ${response.statusText}\n${errorBody}`,
        );
      }

      const responseSet = (await response.json()) as CareerUserResponseSet;

      const { careerResults, tier, careerPathSuggestions } = getSurveyResults({
        userResponseSet: responseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      });

      return {
        userResponseSet: responseSet,
        results: { careerResults, tier, careerPathSuggestions },
      };
    } catch (error) {
      return rejectWithValue(`Failed to get user response set with error: ${error}`);
    }
  },
);

type GetMostResponseSetFromAPIInput = Readonly<{
  careerQuestions: CareerQuestion[];
  careerPaths: CareerPath[];
  careerPathRoles: CareerPathRole[];
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
}>;

/**
 * Fetches the most recent user response set from the API.
 * We then take those results and calculate them. The reducer then listens for this action and updates the store with the results.
 */
export const getMostRecentUserResponseSetFromAPI = createAsyncThunk(
  'userResponse/getMostRecentUserResponseSetFromAPI',
  async (
    {
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
    }: GetMostResponseSetFromAPIInput,
    { rejectWithValue },
  ) => {
    try {
      const response = await fetch(`${ENVIRONMENT_URL}/career-user-response-set/most-recent`, {
        method: 'GET',
      });

      // No blueprint yet (e.g. a visitor who hasn't taken the survey) — not an error.
      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');

        throw new Error(
          `Failed to get most recent response set: ${response.status} ${response.statusText}\n${errorBody}`,
        );
      }

      const apiResponse = (await response.json()) as MostRecentResponseSetAPIResponse;

      const { careerResults, tier, careerPathSuggestions } = getSurveyResults({
        userResponseSet: apiResponse.userResponseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      });

      return {
        userResponseSet: apiResponse.userResponseSet,
        results: { careerResults, tier, careerPathSuggestions },
        aspirationsCategories: apiResponse.aspirations_categories,
      };
    } catch (error) {
      return rejectWithValue(`Failed to get user response set with error: ${error}`);
    }
  },
);
